/**
 * @fileoverview A landing page component to attract and inform new users.
 * It serves as the initial entry point for the application before login.
 */
import React from "react";
import {
  CalendarIcon,
  FacebookIcon,
  InstagramIcon,
  XIcon,
  YouTubeIcon,
  SparklesIcon,
  ChartPieIcon,
} from "./icons";

interface LandingPageProps {
  onLoginClick: () => void;
  onSignUpClick: () => void;
  primaryCtaLabel?: string;
  secondaryCtaLabel?: string;
}

const FeatureCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ icon, title, children }) => (
  <div className="bg-white dark:bg-gray-800/50 p-6 rounded-2xl border border-slate-200 dark:border-gray-700 text-center transform transition-transform duration-300 hover:-translate-y-2">
    <div className="inline-block p-4 bg-orange-100 dark:bg-orange-900/50 rounded-full mb-4">
      {icon}
    </div>
    <h3 className="text-xl font-bold mb-2 text-slate-800 dark:text-gray-100">
      {title}
    </h3>
    <p className="text-slate-500 dark:text-gray-400">{children}</p>
  </div>
);

export const LandingPage: React.FC<LandingPageProps> = ({
  onLoginClick,
  onSignUpClick,
  primaryCtaLabel,
  secondaryCtaLabel,
}) => {
  const [billingCycle, setBillingCycle] = React.useState<"monthly" | "yearly">(
    "yearly"
  );
  const [audienceTab, setAudienceTab] = React.useState<
    "businesses" | "agencies"
  >("businesses");
  const loginLabel = secondaryCtaLabel ?? "Login";
  const signUpLabel = primaryCtaLabel ?? "Sign Up";
  const primaryCta = primaryCtaLabel ?? "Get Started for Free";

  const pricingPlans = [
    {
      id: "standard",
      name: "Standard",
      priceMonthly: 12,
      priceYearly: 10,
      channels: 3,
      teamMembers: 1,
      support: "24/5 email support",
      headlineFeatures: [
        "1 Brand (3 channels)",
        "Facebook Pages, X Profiles, Instagram Business Profiles, LinkedIn Company Pages, Google Business Profile",
        "24/5 email support",
      ],
      includedFeatures: [
        "Home dashboard",
        "Recent posts",
        "Multi-channel publishing",
        "Content scheduling",
        "Publishing calendar",
        "Published/Scheduled posts",
        "zUrl link shortener",
      ],
      featureLabel: "Free features +",
      highlight: false,
    },
    {
      id: "professional",
      name: "Professional",
      priceMonthly: 25,
      priceYearly: 20,
      channels: 9,
      teamMembers: 1,
      support: "24/5 email support",
      headlineFeatures: [
        "1 Brand (9 channels)",
        "1 Team Member",
        "Facebook Pages, X Profiles, Instagram Business Profiles, LinkedIn Company Pages, Google Business Profile",
        "24/5 email support",
      ],
      includedFeatures: [
        "Livestream",
        "Notifications",
        "CustomQ",
        "Repeat posting",
        "Bulk scheduling",
        "Popular posts",
        "Post insights",
        "Bit.ly link shortener",
        "Media library",
      ],
      featureLabel: "Standard features +",
      highlight: true,
    },
    {
      id: "premium",
      name: "Premium",
      priceMonthly: 35,
      priceYearly: 30,
      channels: 13,
      teamMembers: 3,
      support: "24/5 email & live chat support",
      headlineFeatures: [
        "1 Brand (13 channels)",
        "3 Team Members",
        "Facebook Pages, X Profiles, Instagram Business Profiles, LinkedIn Company Pages, Google Business Profile",
        "24/5 priority support",
      ],
      includedFeatures: [
        "SmartQ",
        "Inbox",
        "Content approvals & workflow",
        "Exporting posts",
        "Discussing posts",
        "Collaborate",
        "Team audio/video & chat",
        "Reports dashboard",
        "Custom reports",
      ],
      featureLabel: "Professional features +",
      highlight: false,
    },
  ];

  const freeEditionFeatures = [
    "1 Team Member",
    "1 Brand",
    "zShare Browser Extension",
    "Publishing on 1 channel",
  ];

  const displayedPrice = (plan: (typeof pricingPlans)[number]) =>
    billingCycle === "yearly" ? plan.priceYearly : plan.priceMonthly;

  return (
    <div className="bg-slate-50 dark:bg-gray-900 text-slate-800 dark:text-gray-200">
      {/* Header */}
      <header className="bg-white/80 dark:bg-gray-900/50 backdrop-blur-sm sticky top-0 z-20 border-b border-slate-200 dark:border-gray-700">
        <div className="container mx-auto px-4 md:px-6 py-6 md:py-7 flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center flex-shrink-0">
            <div className="h-16 sm:h-20 w-48 sm:w-60">
              <span
                className="ac-logo"
                role="img"
                aria-label="AmplifyChat logo"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onLoginClick}
              className="font-semibold text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800 py-2 px-4 sm:px-5 rounded-lg transition-colors text-sm"
            >
              {loginLabel}
            </button>
            <button
              onClick={onSignUpClick}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 sm:px-5 rounded-lg transition-transform transform hover:scale-105 text-sm"
            >
              {signUpLabel}
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="py-20 md:py-32">
          <div className="container mx-auto px-4 md:px-6 text-center">
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 text-slate-900 dark:text-white">
              The Simple Way to Manage Social Media
            </h1>
            <p className="max-w-3xl mx-auto text-lg md:text-xl text-slate-600 dark:text-gray-300 mb-8">
              Schedule posts, generate content with AI, and analyze your
              performance across all platforms. Effortlessly.
            </p>
            <button
              onClick={onSignUpClick}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-6 text-base sm:py-4 sm:px-8 sm:text-lg rounded-lg transition-transform transform hover:scale-105"
            >
              {primaryCta}
            </button>

            <div className="mt-16 max-w-5xl mx-auto bg-white dark:bg-gray-800 p-2 sm:p-4 rounded-2xl shadow-2xl border border-slate-200 dark:border-gray-700">
              <div className="aspect-video rounded-xl overflow-hidden">
                <img
                  src="/assets/hero.png"
                  alt="Social media command center with dashboards for multiple platforms"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="mt-12 sm:mt-16 text-center">
              <p className="text-sm font-semibold text-slate-500 dark:text-gray-400 mb-4">
                WORKS WITH YOUR FAVORITE PLATFORMS
              </p>
              <div className="flex flex-wrap justify-center items-center gap-6 text-slate-600 dark:text-gray-300">
                <FacebookIcon className="w-8 h-8 text-[#1877F2]" />
                <InstagramIcon className="w-8 h-8 text-[#E1306C]" />
                <XIcon className="w-7 h-7 text-black dark:text-white" />
                <YouTubeIcon className="w-9 h-9 text-[#FF0000]" />
                <span className="text-base font-semibold uppercase tracking-wide">
                  and more...
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 md:py-24 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
                Everything you need, all in one place
              </h2>
              <p className="mt-4 max-w-2xl mx-auto text-slate-600 dark:text-gray-300">
                Stop switching between tabs. Manage your entire social media
                strategy from a single, powerful dashboard.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
              <FeatureCard
                icon={<SparklesIcon className="w-8 h-8 text-orange-500" />}
                title="AI-Powered Content"
              >
                Effortlessly create engaging content with AI suggestions and
                refinement tools powered by Gemini.
              </FeatureCard>
              <FeatureCard
                icon={<CalendarIcon className="w-8 h-8 text-orange-500" />}
                title="Advanced Scheduling"
              >
                Plan your content calendar with our intuitive drag-and-drop
                scheduler and automated publishing.
              </FeatureCard>
              <FeatureCard
                icon={<ChartPieIcon className="w-8 h-8 text-orange-500" />}
                title="Performance Analytics"
              >
                Track your growth with easy-to-understand analytics and
                reporting for every post and profile.
              </FeatureCard>
              <FeatureCard
                icon={<SparklesIcon className="w-8 h-8 text-orange-500" />}
                title="Collaboration"
              >
                Work together, work better.
                <br />
                Manage multiple brands and team roles efficiently.
              </FeatureCard>
            </div>
          </div>
        </section>

        {/* Feature Dive Section */}
        <section className="py-20 md:py-24">
          <div className="container mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
                Never run out of ideas
              </h2>
              <p className="mt-4 text-slate-600 dark:text-gray-300 text-lg">
                Beat writer's block with our integrated AI assistant. Generate
                fresh post ideas, refine your drafts, and even create alt-text
                for your images, all without leaving the composer.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-2 sm:p-4 rounded-2xl shadow-xl border border-slate-200 dark:border-gray-700">
              <div className="aspect-video rounded-xl overflow-hidden">
                <img
                  src="/assets/datacenter4.png"
                  alt="AmplifyChat composer interface illustrating collaboration features"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 md:py-24 bg-slate-100 dark:bg-gray-900/60">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center mb-12 space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
                Pricing plans
              </h2>
              <div className="inline-flex items-center gap-3 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-full p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => setAudienceTab("businesses")}
                  className={`px-4 py-2 text-sm font-semibold rounded-full transition ${
                    audienceTab === "businesses"
                      ? "bg-blue-600 text-white shadow"
                      : "text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-700/60"
                  }`}
                >
                  For Businesses
                </button>
                <button
                  type="button"
                  onClick={() => setAudienceTab("agencies")}
                  className={`px-4 py-2 text-sm font-semibold rounded-full transition ${
                    audienceTab === "agencies"
                      ? "bg-blue-600 text-white shadow"
                      : "text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-700/60"
                  }`}
                >
                  For Agencies
                </button>
              </div>

              <div className="flex items-center justify-center gap-3">
                <span
                  className={`text-sm font-semibold ${
                    billingCycle === "monthly"
                      ? "text-blue-600 dark:text-blue-400"
                      : "text-slate-500 dark:text-gray-400"
                  }`}
                >
                  Monthly
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setBillingCycle((prev) =>
                      prev === "monthly" ? "yearly" : "monthly"
                    )
                  }
                  className="relative inline-flex h-6 w-12 items-center rounded-full bg-blue-600 transition"
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                      billingCycle === "yearly"
                        ? "translate-x-6"
                        : "translate-x-1"
                    }`}
                  />
                </button>
                <span
                  className={`text-sm font-semibold ${
                    billingCycle === "yearly"
                      ? "text-blue-600 dark:text-blue-400"
                      : "text-slate-500 dark:text-gray-400"
                  }`}
                >
                  Yearly
                </span>
                <span className="ml-2 text-xs font-semibold uppercase tracking-wide bg-amber-200 text-amber-700 px-2 py-1 rounded-full">
                  Save 30%
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
              {pricingPlans.map((plan) => {
                const price = displayedPrice(plan);
                const isYearly = billingCycle === "yearly";
                return (
                  <div
                    key={plan.id}
                    className={`relative flex flex-col rounded-3xl border shadow-lg overflow-hidden ${
                      plan.highlight
                        ? "border-blue-200 dark:border-blue-800 bg-gradient-to-b from-blue-50 via-white to-white dark:from-blue-900/40 dark:via-gray-900 dark:to-gray-900"
                        : "border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900"
                    }`}
                  >
                    {plan.highlight && (
                      <div className="absolute right-0 top-0 rounded-bl-3xl bg-blue-600 text-white text-xs font-semibold px-4 py-2">
                        Most Popular
                      </div>
                    )}
                    <div className="p-6 sm:p-8 space-y-6">
                      <div className="space-y-2">
                        <span className="inline-block text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/40 px-3 py-1 rounded-full">
                          {plan.name}
                        </span>
                        <div className="flex items-end gap-2">
                          <span className="text-4xl font-extrabold text-slate-900 dark:text-white">
                            ${price}
                          </span>
                          <span className="text-sm text-slate-500 dark:text-gray-400">
                            /month{" "}
                            {isYearly ? "billed annually" : "billed monthly"}
                          </span>
                        </div>
                      </div>

                      <ul className="space-y-2 text-sm text-slate-600 dark:text-gray-300">
                        {plan.headlineFeatures.map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <span className="mt-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300">
                              ✓
                            </span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="border-t border-slate-200 dark:border-gray-800 p-6 sm:p-8 bg-slate-50 dark:bg-gray-900/70">
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
                        {plan.featureLabel}
                      </h3>
                      <ul className="space-y-2 text-sm text-slate-600 dark:text-gray-300">
                        {plan.includedFeatures.map((feature) => (
                          <li key={feature} className="flex items-start gap-2">
                            <span className="mt-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300">
                              ✓
                            </span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-12">
              <div className="rounded-3xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-lg p-8 sm:p-10 text-center">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                  Free edition
                </h3>
                <p className="mt-3 text-sm text-slate-600 dark:text-gray-300 max-w-2xl mx-auto">
                  Just getting started? Launch your social presence with our
                  forever-free plan.
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-sm font-semibold text-slate-700 dark:text-gray-200">
                  {freeEditionFeatures.map((feature) => (
                    <span key={feature} className="flex items-center gap-2">
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300">
                        ✓
                      </span>
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 md:py-24 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4 md:px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              Ready to Amplify Your Social Presence?
            </h2>
            <p className="mt-4 max-w-xl mx-auto text-slate-600 dark:text-gray-300">
              Join thousands of creators and brands growing their audience with
              AmplifyChat.
            </p>
            <button
              onClick={onSignUpClick}
              className="mt-8 bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-6 text-base sm:py-4 sm:px-8 sm:text-lg rounded-lg transition-transform transform hover:scale-105"
            >
              {primaryCtaLabel ?? "Sign Up Now"}
            </button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-gray-700">
        <div className="container mx-auto px-4 md:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-slate-500 dark:text-gray-400 text-sm">
          <span>
            &copy; {new Date().getFullYear()} AmplifyChat. All Rights Reserved.
          </span>
          <a
            href="/privacy.html"
            className="text-blue-600 dark:text-blue-400 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </div>
      </footer>
    </div>
  );
};
